import { inject, Injectable } from '@angular/core';
import { catchError, map, Observable, of, switchMap, tap } from 'rxjs';
import {
  BankBranch,
  Invoice,
  Receipt,
  ReceiptAdvance,
  ReceiptCashInput,
  ReceiptCashRequest,
  ReceiptCheckInput,
  ReceiptCheckRequest,
  ReceiptIncome,
  ReceiptIncomeType,
} from '../interfaces/receipt.model';
import { ReceiptsService } from '../services/receipts.service';
import {
  cashTotal,
  formatMoney,
  incomeFromReceipt,
  invoiceResiduo,
  invoiceResiduoDaCoprire,
  normalizeInvoice,
  roundMoney,
  toCashRequest,
  toCheckRequest,
} from '../utils/receipt.utils';

export interface CheckValidationResult {
  error: string | null;
  banca: string | null;
}

const emptyIncome = (): ReceiptIncome => ({
  incomeType: ReceiptIncomeType.None,
  cash: null,
  check: null,
  acconti: [],
});

@Injectable()
export class ReceiptEditStore {
  private readonly service = inject(ReceiptsService);

  idBusinessPartner = '';
  idIncasso: string | null = null;
  numeroIncasso = 0;
  email: string | null = null;
  invoices: Invoice[] = [];
  income: ReceiptIncome = emptyIncome();
  message: string | null = null;
  isSaving = false;

  revision = 0;

  init(params: {
    idBusinessPartner: string;
    receipt?: Receipt | null;
    openInvoices?: Invoice[];
    acconti?: ReceiptAdvance[];
  }): void {
    this.idBusinessPartner = params.idBusinessPartner;
    this.idIncasso = params.receipt?.id ?? null;
    this.numeroIncasso = params.receipt?.numeroIncasso ?? 0;
    this.invoices = (params.openInvoices ?? []).map(normalizeInvoice);
    this.income = {
      ...emptyIncome(),
      ...incomeFromReceipt(params.receipt),
      acconti: params.acconti ?? [],
    };
    this.email = null;
    this.message = null;
    this.isSaving = false;
    this.revision = 0;
  }

  get isPersisted(): boolean {
    return !!this.idIncasso;
  }

  get totaleIncasso(): number {
    switch (this.income.incomeType) {
      case ReceiptIncomeType.Cash:
        return cashTotal(this.income.cash);
      case ReceiptIncomeType.Check:
        return roundMoney(this.income.check?.importo ?? 0);
      default:
        return 0;
    }
  }

  get totaleAcconti(): number {
    return roundMoney(this.income.acconti.reduce((sum, a) => sum + (a.importo || 0), 0));
  }

  get totaleAssegnato(): number {
    return roundMoney(this.invoices.reduce((sum, i) => sum + (i.importoAssegnato || 0), 0));
  }

  get coperturaTotale(): number {
    return roundMoney(this.totaleAssegnato + this.totaleAcconti);
  }

  get residuoDisponibile(): number {
    return roundMoney(this.totaleIncasso - this.coperturaTotale);
  }

  get residuoPrimaDegliAcconti(): number {
    return roundMoney(this.totaleIncasso - this.totaleAssegnato);
  }

  get hasAssegnazioni(): boolean {
    return this.totaleAssegnato > 0;
  }

  get canConfirm(): boolean {
    return this.totaleIncasso > 0 && this.residuoDisponibile === 0 && this.hasAssegnazioni;
  }

  clearMessage(): void {
    this.message = null;
  }


  setIncomeCash(cash: ReceiptCashInput | null): Observable<boolean> {
    const isEmpty = !cash || cashTotal(cash) <= 0;

    const income: ReceiptIncome = {
      ...this.income,
      incomeType: isEmpty ? ReceiptIncomeType.None : ReceiptIncomeType.Cash,
      cash: isEmpty ? null : cash,
      check: null,
    };

    if (isEmpty || !cash) {
      this.applyIncome(income);
      return of(true);
    }

    return this.save(income, { contanti: toCashRequest(cash) });
  }

  setIncomeCheck(check: ReceiptCheckInput | null): Observable<boolean> {
    const income: ReceiptIncome = {
      ...this.income,
      incomeType: check ? ReceiptIncomeType.Check : ReceiptIncomeType.None,
      check: check,
      cash: null,
    };

    if (!check) {
      this.applyIncome(income);
      return of(true);
    }

    return this.save(income, { assegno: toCheckRequest(check) });
  }

  validateCheck(check: ReceiptCheckInput): Observable<CheckValidationResult> {
    const soloCifre = /^\d+$/;

    if (!check.data) {
      return of(this.checkError('Specificare la data assegno'));
    }

    const oggi = new Date();
    const soloData = new Date(
      check.data.getFullYear(),
      check.data.getMonth(),
      check.data.getDate(),
    );

    if (soloData.getTime() > oggi.getTime() || oggi.getFullYear() - check.data.getFullYear() > 2) {
      return of(this.checkError('La data assegno deve rispettare i vincoli di validità'));
    }

    if (!soloCifre.test(check.abi)) {
      return of(this.checkError('Il campo ABI è obbligatorio e può essere formato solo da numeri'));
    }

    if (!soloCifre.test(check.cab)) {
      return of(this.checkError('Il campo CAB è obbligatorio e può essere formato solo da numeri'));
    }

    if (!soloCifre.test(check.numeroAssegno)) {
      return of(
        this.checkError(
          'Il campo numero assegno è obbligatorio e può essere formato solo da numeri',
        ),
      );
    }

    if (check.importo <= 0 || !/^\d+(\.\d{1,2})?$/.test(String(check.importo))) {
      return of(
        this.checkError(
          'Il campo importo deve rappresentare un numero decimale valido per la valuta',
        ),
      );
    }

    if (!check.tipo) {
      return of(this.checkError('Specificare il tipo di assegno'));
    }

    if (check.banca) {
      return of({ error: null, banca: check.banca });
    }

    return this.lookupBank(check.abi, check.cab);
  }

  lookupBank(abi: string, cab: string): Observable<CheckValidationResult> {
    return this.service.getFilialiBancarie(abi, cab).pipe(
      map((filiali: BankBranch[]) =>
        filiali.length === 0
          ? this.checkError('Inserire un ABI e CAB corrispondente ad una banca.')
          : { error: null, banca: filiali[0].banca },
      ),
      catchError(() => of(this.checkError('Verifica ABI/CAB non riuscita.'))),
    );
  }


  setAcconti(acconti: ReceiptAdvance[]): void {
    const totale = roundMoney(acconti.reduce((sum, a) => sum + (a.importo || 0), 0));

    if (totale > this.residuoPrimaDegliAcconti) {
      this.message =
        `Gli acconti (${formatMoney(totale)}) superano quanto resta da attribuire ` +
        `(${formatMoney(this.residuoPrimaDegliAcconti)}).`;
      return;
    }

    this.applyIncome({ ...this.income, acconti });
  }


  assignAmount(index: number, value: number): void {
    const invoice = this.invoices[index];

    if (!invoice) {
      return;
    }

    const importo = roundMoney(value);

    if (importo < 0) {
      this.reject(index, "L'importo assegnato non può essere negativo.");
      return;
    }

    const residuo = invoiceResiduo(invoice);

    if (importo > residuo) {
      this.reject(
        index,
        `Importo assegnato superiore al residuo della partita (${formatMoney(residuo)}).`,
      );
      return;
    }

    const coperturaAltrePartite = roundMoney(this.totaleAssegnato - invoice.importoAssegnato);
    const coperturaRichiesta = roundMoney(coperturaAltrePartite + importo + this.totaleAcconti);

    if (coperturaRichiesta > this.totaleIncasso) {
      const disponibile = roundMoney(
        this.totaleIncasso - coperturaAltrePartite - this.totaleAcconti,
      );

      this.reject(
        index,
        "Il totale assegnato supera il totale dell'incasso. " +
          `Su questa partita restano disponibili ${formatMoney(Math.max(0, disponibile))}.`,
      );
      return;
    }

    this.replaceInvoice(index, { ...invoice, importoAssegnato: importo });
  }

  saldaInvoice(index: number): void {
    const invoice = this.invoices[index];

    if (!invoice) {
      return;
    }

    const differenza = invoiceResiduoDaCoprire(invoice) + invoice.importoAssegnato;

    if (differenza <= 0) {
      return;
    }

    const residuoDisponibile = this.residuoDisponibile + invoice.importoAssegnato;

    if (differenza > residuoDisponibile) {
      const parziale = Math.max(0, Math.min(differenza, residuoDisponibile));

      this.replaceInvoice(index, { ...invoice, importoAssegnato: parziale, dataSaldo: null });
      return;
    }

    this.replaceInvoice(index, {
      ...invoice,
      importoAssegnato: invoiceResiduo(invoice),
      dataSaldo: new Date().toISOString(),
    });
  }

  resetAmount(index: number): void {
    const invoice = this.invoices[index];

    if (!invoice) {
      return;
    }

    this.replaceInvoice(index, { ...invoice, importoAssegnato: 0 });
  }


  delete(): Observable<boolean> {
    if (!this.idIncasso) {
      return of(true);
    }

    this.isSaving = true;
    this.message = null;

    return this.service.deleteReceipt(this.idIncasso).pipe(
      map(() => {
        this.isSaving = false;
        return true;
      }),
      catchError(() => {
        this.fail('Eliminazione non riuscita.');
        return of(false);
      }),
    );
  }

  sendReceipt(): Observable<boolean> {
    const idIncasso = this.idIncasso;

    if (!this.canConfirm || !idIncasso) {
      return of(false);
    }

    this.isSaving = true;
    this.message = null;

    const assegnate = this.invoices.filter(invoice => invoice.importoAssegnato > 0);

    return this.service.assignInvoices(idIncasso, assegnate).pipe(
      switchMap(() =>
        this.service.confirmReceipt(idIncasso, this.income.acconti).pipe(
          catchError(() => {
            this.fail('Conferma non riuscita. Le assegnazioni restano salvate, riprovare.');
            return of(null);
          }),
        ),
      ),
      switchMap(response => {
        if (!response) {
          return of(false);
        }

        this.email = response.emailInviata ? response.emailDestinatario ?? null : null;

        if (this.numeroIncasso > 0) {
          return of(true);
        }

        return this.service.getReceipt({ idIncasso }).pipe(
          map(receipt => {
            if (receipt) {
              this.numeroIncasso = receipt.numeroIncasso;
            }

            return true;
          }),
          catchError(() => of(true)),
        );
      }),
      tap(ok => {
        if (ok) {
          this.isSaving = false;
        }
      }),
      catchError(() => {
        this.fail('Assegnazione delle partite non riuscita.');
        return of(false);
      }),
    );
  }

  downloadReceiptPdf(): Observable<Blob | null> {
    const idIncasso = this.idIncasso;

    if (!idIncasso) {
      return of(null);
    }

    return this.service.downloadReceiptPdf(idIncasso).pipe(
      catchError(() => {
        this.message = 'Ricevuta non disponibile.';
        return of(null);
      }),
    );
  }


  private save(
    income: ReceiptIncome,
    payload: { contanti?: ReceiptCashRequest; assegno?: ReceiptCheckRequest },
  ): Observable<boolean> {
    this.isSaving = true;
    this.message = null;

    return this.service
      .registerReceipt({
        idIncasso: this.idIncasso,
        idBusinessPartner: this.idBusinessPartner,
        ...payload,
      })
      .pipe(
        map(response => {
          this.idIncasso = response?.idIncasso ?? this.idIncasso;
          this.isSaving = false;
          this.applyIncome(income);
          return true;
        }),
        catchError(() => {
          this.fail('Salvataggio non riuscito.');
          return of(false);
        }),
      );
  }

  private applyIncome(income: ReceiptIncome): void {
    this.income = income;
    this.message = null;

    if (this.residuoDisponibile < 0) {
      this.message =
        `Il totale incasso è inferiore a quanto già assegnato ` +
        `(${formatMoney(this.coperturaTotale)}). Rivedere le assegnazioni alle partite ` +
        `oppure gli acconti.`;
    }
  }

  private reject(index: number, message: string): void {
    this.invoices = this.invoices.map((invoice, i) =>
      i === index ? { ...invoice, importoAssegnato: 0 } : invoice,
    );
    this.message = message;
    this.revision += 1;
  }

  private replaceInvoice(index: number, invoice: Invoice): void {
    this.invoices = this.invoices.map((current, i) => (i === index ? invoice : current));
    this.message = null;
  }

  private checkError(error: string): CheckValidationResult {
    return { error, banca: null };
  }

  private fail(message: string): void {
    this.isSaving = false;
    this.message = message;
  }
}
